import { useCallback } from 'react';

import { useApplicationConfig } from '@bonusly/application';
import { useFeatureFlag } from '@bonusly/who-am-i';

import { AppPath, Route } from '../../top-navbar/config/router';
import { pathWithId } from '../../top-navbar/utils/api';

/**
 * Hook to get the profile path/URL for a user.
 *
 * The canonical profile path is `/company/users/:id` for both the new SPA and the monolith.
 * They do not coexist - when the feature flag is enabled, the SPA handles the route;
 * when disabled, FlaggedProfilePage redirects to the monolith.
 *
 * When 'new-profile-page' flag is enabled: Returns the SPA route path (/company/users/:id)
 * When flag is disabled: Returns the full monolith URL (recognizeOrigin + /company/users/:id)
 *
 * @returns Object with:
 *   - isNewProfileEnabled: boolean indicating if the new profile is enabled
 *   - getProfilePath: function that returns the relative path (for React Router navigation)
 *   - getProfileUrl: function that returns the full URL (for external links or href attributes)
 *   - isExternalLink: boolean indicating if links should navigate externally (when flag is off)
 */
export const useProfilePath = () => {
	const isNewProfileEnabled = useFeatureFlag('new-profile-page');
	const recognizeOrigin = useApplicationConfig('recognizeOrigin');

	// Returns the path - use with components like UserNavLink that handle URL building
	const getProfilePath = useCallback(
		(userId: string): string => {
			if (isNewProfileEnabled) {
				return pathWithId(AppPath.Profile, userId);
			}
			return pathWithId(Route.UserProfile, userId);
		},
		[isNewProfileEnabled]
	);

	// Returns the full URL - use for direct href attributes on <a> or <Link> elements
	// Always returns a full URL with origin for consistency
	const getProfileUrl = useCallback(
		(userId: string): string => {
			const path = isNewProfileEnabled ? pathWithId(AppPath.Profile, userId) : pathWithId(Route.UserProfile, userId);
			return `${recognizeOrigin}${path}`;
		},
		[isNewProfileEnabled, recognizeOrigin]
	);

	return {
		isNewProfileEnabled,
		getProfilePath,
		getProfileUrl,
		isExternalLink: !isNewProfileEnabled,
	};
};
